import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, TSyncfield} from "../../index";
import {ObjectTypeId} from "../../constants";
import {global, lm} from "../../appContext";
import {IDialogPickObjectParams} from "../object/dialog/pick";
import {nsSyncFields} from "../../req/nsSyncFields";
import {createErrorMessage, getObjectDefinition, getObjectDisplayName} from "../../util/misc";
import {nsFa} from "../fa/fn";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {nsTooltip} from "../../util/nsTooltip";
import {PLEASE_WAIT_MESSAGE} from "../../constants/Language";
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import i = nsFa.i;
import user_anyHtml from './user-any.html?raw';

export const template: string = user_anyHtml;

export interface IParams_pick_user_any<T> {
    additionalSyncfieldNames?: TSyncfield[]
    icon?: string
    input_attr?: object
    dialogPickObjectParams?: Partial<IDialogPickObjectParams>
    onSelect?: (o: T) => void
    selectedObject?: KnockoutObservable<TObjectData_basic | null>
    input?: KnockoutObservable<string>
    promiseInitial?: () => Promise<TObjectData_basic>
}

export class viewModel<T = TObjectData_basic> {
    private isReady: KnockoutObservable<boolean | null> = ko.observable(null)
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private params: IParams_pick_user_any<TObjectData_basic>
    private input: KnockoutObservable<string | null> = ko.observable(null)
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private showWarning: KnockoutObservable<boolean> = ko.observable(false)
    private showInfo: KnockoutObservable<boolean> = ko.observable(false)
    private isSilent: KnockoutObservable<boolean> = ko.observable(false)

    constructor(params: IParams_pick_user_any<TObjectData_basic>) {
        this.params = _.defaultsDeep(params, {
            icon: 'fa-user',
            input: ko.observable(''),
            selectedObject: ko.observable(null),
        })
        this.kosub.subscribe(this.input, s => {
            this.params.input?.(s ?? '')
        })
        if (this.params.input) {
            this.kosub.subscribe(this.params.input, s => {
                if (!this.isSilent()) {
                    this.input(s)
                }
            })
        }
        this.input(this.params.input?.() ?? '')
        this.init()
    }

    private init() {
        if (!this.params.promiseInitial) {
            this.isReady(true)
            return
        }
        this.messageData({message: PLEASE_WAIT_MESSAGE})
        this.params
            .promiseInitial()
            .then(obj => {
                if (obj) {
                    this.onSelection(obj)
                }
                this.messageData(null)
                this.isReady(true)
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }

    private initWrapperBindings() {
        return {
            css: {'show-warning': this.showWarning, 'show-info': this.showInfo},
        }
    }

    private initInputBindings() {
        return this.params.input_attr ?? {
            attr: {placeholder: lm.t('choose subject|s:((user|ac))') ?? '', title: ko.pureComputed(() => {
                const selected = this.params.selectedObject?.();
                if (!selected) {
                    return '';
                }
                const titleKey = nsTooltip.getTitle_objectdata(selected);
                return titleKey ? (lm.t(titleKey) ?? '') : '';
            })},
            textInput: this.input,
            event: {
                'focusout': () => {
                    if (!this.input()) {
                        this.showInfo(false)
                        this.showWarning(true)
                        return
                    }
                    if (this.input() && !this.params.selectedObject?.()) {
                        this.showWarning(false)
                        this.showInfo(true)
                    }
                },
                'input': () => {
                    this.params.selectedObject?.(null)
                }
            }
        }
    }

    private onPickObjectClick() {
        const objDef = getObjectDefinition(ObjectTypeId.User)
        if (!objDef) {
            return;
        }
        const params: Partial<IDialogPickObjectParams> = _.defaultsDeep(
            this.params.dialogPickObjectParams,
            {
                selectedObject: this.params.selectedObject,
                objecttypeid: ObjectTypeId.User,
                onObjectSelected: (obj: TObjectData_basic) => this.onSelection(obj),
            })
        global.dialogManager.addComponent({
            title: `${i(objDef.icon)} ${lm.t(`choose subject|s:((${objDef.name}|ac))`)}`,
            content: {
                name: 'component/object/dialog/pick',
                params: params,
            }
        });

    }

    private onSelection(obj: TObjectData_basic) {
        this.isSilent(true)
        const name = getObjectDisplayName(obj);
        this.input(name)
        this.params.selectedObject?.(obj)
        this.params.onSelect?.(obj)
        this.isSilent(false)
        this.showInfo(false)
        this.showWarning(false)
    }

    dispose() {
        this.kosub.unsuscribeAll()
    }

}